import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ShieldCheck, MapPin, MessageSquareOff, IndianRupee, Camera, FileCheck2, Users, AlertTriangle } from "lucide-react";

const BUYER_TIPS = [
  { icon: MapPin, title: "Always meet in person", body: "Inspect the property, vehicle or item at the actual address. Prefer daylight visits and take a friend along if it&apos;s your first meeting." },
  { icon: IndianRupee, title: "Never pay a large sum upfront", body: "A small token amount only after physical verification is fair. A seller who demands full payment before any meeting is almost always a scam." },
  { icon: FileCheck2, title: "Verify the paperwork", body: "Ask for the title deed, RC book, insurance, GST certificate, or license — whichever applies. Real sellers keep these ready." },
  { icon: MessageSquareOff, title: "Ignore off-platform pressure", body: "Requests to shift the deal to Telegram, random UPI ids or gift cards are red flags. Stay on The Pataudi&apos;s messaging trail for your own paper record." },
];

const SELLER_TIPS = [
  { icon: Camera, title: "Post real photos", body: "Bright, honest photos of the actual item build trust fast. Stolen photos from Google or Instagram get your listing removed." },
  { icon: ShieldCheck, title: "Never share OTPs", body: "No genuine buyer needs the OTP from your phone or bank app. Anyone asking for one is trying to hijack your account or empty your wallet." },
  { icon: Users, title: "Meet at a safe location", body: "For test drives or site visits, share your live location with a family member and prefer a busy area or a police station&apos;s vicinity." },
  { icon: AlertTriangle, title: "Beware of over-payment scams", body: "A buyer who transfers extra by &apos;mistake&apos; and asks you to refund the difference is running a classic bounced-cheque scam." },
];

export default function SafetyTips() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="safety-tips-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-14">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">Trust & Safety</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Safety Tips</h1>
            <p className="text-white/70 mt-3 text-base max-w-2xl">
              A short field guide for buying and selling on The Pataudi without getting burnt. Read it once — it will save
              you thousands.
            </p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 space-y-14">
          <section>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-6">For buyers</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {BUYER_TIPS.map((t) => (
                <article key={t.title} className="p-5 rounded-2xl border border-neutral-200 bg-white hover:border-primary/30 transition-colors" data-testid={`buyer-tip-${t.title}`}>
                  <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <t.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-ink text-lg">{t.title}</h3>
                  <p className="text-sm text-ink/70 mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                </article>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-6">For sellers</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {SELLER_TIPS.map((t) => (
                <article key={t.title} className="p-5 rounded-2xl border border-neutral-200 bg-white hover:border-primary/30 transition-colors" data-testid={`seller-tip-${t.title}`}>
                  <div className="h-11 w-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                    <t.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-ink text-lg">{t.title}</h3>
                  <p className="text-sm text-ink/70 mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-2xl bg-primary/5 border border-primary/15 p-6 md:p-8">
            <h2 className="font-display text-xl md:text-2xl font-bold text-ink">Report a suspicious listing</h2>
            <p className="text-ink/70 mt-2 leading-relaxed max-w-3xl">
              If a price looks too good to be true, if photos feel copied, or if a seller pushes you to move off-platform,
              please tell us. One quick note keeps the whole community safer.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="mailto:sales@thepataudi.com" className="inline-flex items-center h-11 px-5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors" data-testid="safety-email-btn">
                Email us
              </a>
              <a href="https://wa.me/919467458749" target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-11 px-5 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:brightness-95 transition-colors" data-testid="safety-whatsapp-btn">
                WhatsApp +91 9467458749
              </a>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-4">What The Pataudi does for you</h2>
            <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
              <li>Every listing is reviewed by our team before it goes live.</li>
              <li>Owner contact details are hidden by default and unlock only after a small credit spend — bots can&apos;t scrape phone numbers in bulk.</li>
              <li>QR codes, UPI ids and payment screenshots inside listing photos are automatically blocked.</li>
              <li>Repeat offenders are banned across the platform, not just the single listing.</li>
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
